import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import useSeo from "@/hooks/useSeo";
import MangaCard from "@/components/base/MangaCard";
import { mangaList } from "@/mocks/manga";

const genres = ["全部", "科幻", "治愈", "玄幻", "武侠", "恋爱", "悬疑", "搞笑", "奇幻", "热血"];

const guides = [
  { icon: "ri-search-eye-line", title: "看评分也看追更", desc: "评分反映口碑，追更进度体现粘度，两者结合判断更准。" },
  { icon: "ri-focus-3-line", title: "锁定你的偏好", desc: "先确定喜欢的类型与画风，再用筛选快速缩小范围。" },
  { icon: "ri-timer-flash-line", title: "留意更新频率", desc: "稳定更新的作品更适合长期追，断更风险也更低。" },
  { icon: "ri-chat-smile-3-line", title: "参考社区讨论", desc: "去评论区看看真实读者的反馈，避免踩雷冷门烂尾。" },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "笨狗漫画网编辑推荐",
    url: "https://comicsur.github.io/recommend",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://comicsur.github.io/" },
      { "@type": "ListItem", position: 2, name: "编辑推荐", item: "https://comicsur.github.io/recommend" },
    ],
  },
];

export default function RecommendPage() {
  const [genre, setGenre] = useState("全部");
  const list = genre === "全部" ? mangaList : mangaList.filter((m) => m.genre === genre);

  useSeo({
    title: "编辑推荐漫画 - 编辑部精选追更指南 | 笨狗漫画网",
    description:
      "笨狗漫画网编辑推荐栏目，由编辑部精选值得追的漫画作品，附追更建议与类型筛选，帮你找到下一部心头好。",
    jsonLd,
  });

  return (
    <PageLayout>
      <div className="bg-dots">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-600" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
            <span className="text-foreground-950">编辑推荐</span>
          </nav>

          <div className="mt-4 max-w-2xl">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">编辑部本周力荐</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              每期由编辑部从剧情、画风、口碑与更新稳定性多个维度精选，用心为你挑出值得花时间追的漫画，追更不踩雷。
            </p>
          </div>

          <div className="mt-6 flex w-max max-w-full flex-wrap items-center gap-2 rounded-full bg-background-100 p-1.5">
            {genres.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGenre(g)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  genre === g ? "bg-accent-500 text-background-50" : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {list.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>

          <div className="mt-14 rounded-3xl bg-primary-500 p-6 md:p-8">
            <h2 className="font-heading text-2xl text-background-50 md:text-3xl">编辑部追更小贴士</h2>
            <p className="mt-2 text-sm text-primary-100">学会这几招，选漫效率直接翻倍</p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {guides.map((g) => (
                <div key={g.title} className="rounded-2xl bg-background-50/10 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50/20 text-background-50">
                    <i className={`${g.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3 text-base font-bold text-background-50">{g.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-primary-100">{g.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}